<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onScopeDispose,
    ref,
    watch,
  } from 'vue';
  import { useNow } from '@vueuse/core';
  import { isNavigationFailure } from 'vue-router';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ReservationsTabs from '@/components/v2/reservations/V2ReservationsTabs.vue';
  import V2ReservationsHero from '@/components/v2/reservations/V2ReservationsHero.vue';
  import V2ReservationsSettingsTab from '@/components/v2/reservations/V2ReservationsSettingsTab.vue';
  import V2ReservationsDayTab from '@/components/v2/reservations/day/V2ReservationsDayTab.vue';
  import V2ReservationsAgendaTab from '@/components/v2/reservations/V2ReservationsAgendaTab.vue';
  import V2ReservationsWaitlistTab from '@/components/v2/reservations/V2ReservationsWaitlistTab.vue';
  import V2ReservationsGuestsTab from '@/components/v2/reservations/V2ReservationsGuestsTab.vue';
  import V2ReservationsReportsTab from '@/components/v2/reservations/V2ReservationsReportsTab.vue';
  import V2ReservationDetailPanel from '@/components/v2/reservations/V2ReservationDetailPanel.vue';
  import V2ReservationFormModal from '@/components/modals/reservations/V2ReservationFormModal.vue';
  import V2ReservationWalkInModal from '@/components/modals/reservations/V2ReservationWalkInModal.vue';
  import V2ReservationGuestModal from '@/components/modals/reservations/V2ReservationGuestModal.vue';
  import V2ReservationWaitlistAddModal from '@/components/modals/reservations/V2ReservationWaitlistAddModal.vue';
  import V2ReservationsGuide from '@/components/v2/reservations/guide/V2ReservationsGuide.vue';
  import type { IReservationGuideExitTarget } from '@/composition/reservations/reservation-guide-context';
  import { useAuthStore } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { deviceCanEverPrint, usePrinting } from '@/composition/use-printing';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { confirmReservationBranchLeave } from '@/composition/reservations/reservation-branch-leave';
  import { useReservationGuideAutoOpen } from '@/composition/reservations/use-reservation-guide-auto-open';
  import { isReservationSetupIncomplete } from '@/domain/reservations/reservation-setup';
  import { useReservationsShortcuts } from '@/composition/reservations/use-reservations-shortcuts';
  import { vueData } from '@/VueData';
  import {
    addDays,
    defaultBoardDate,
    getReservationTimeZone,
  } from '@/domain/reservations/reservation-time';
  import { buildReservationsDayPrintOps } from '@/domain/reservations/reservations-print';
  import { PermissionType } from '@/enums/PermissionType';
  import type { IReservation } from '@/interfaces/IReservation';
  import { PrinterType } from '@/modules/PrintService';
  import { useReservationsStore } from '@/stores/reservations.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  interface IFormPrefill {
    date?: string;
    startsAt?: string;
    tableIds?: number[];
    partySize?: number;
    guest?: {
      customerId?: number | null;
      name: string;
      phoneE164?: string | null;
      email?: string | null;
    };
    reservationToEdit?: IReservation;
  }
  const proxy = getCurrentInstance()!.proxy;
  const restaurant = useRestaurantStore();
  const store = useReservationsStore();
  const auth = useAuthStore();
  const modals = useModal();
  const ui = useUi();
  const printing = usePrinting();
  const money = usePosMoney();
  const { width } = useStableWindowSize();
  const now = useNow({ interval: 30_000 });
  const waitlistCount = ref<number | null>(null);
  const guestsTab = ref<{ reload: () => Promise<void> } | null>(null);
  const dayTab = ref<{ focusSearch: () => Promise<void> | undefined } | null>(
    null
  );
  const canManage = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsManage)
  );
  const canSettings = computed(() =>
    auth.permissionSet.has(PermissionType.ReservationsSettings)
  );
  const tabs = computed(() => [
    ...(canManage.value
      ? ['today', 'agenda', 'waitlist', 'guests', 'reports']
      : []),
    ...(canSettings.value ? ['settings'] : []),
  ]);
  const tab = computed(() => {
    const requested = proxy.$route.query.tab;
    return typeof requested === 'string' && tabs.value.includes(requested)
      ? requested
      : tabs.value[0] ?? 'today';
  });
  const zone = computed(() =>
    getReservationTimeZone(
      restaurant.selectedBranch?.location,
      restaurant.restaurant?.country
    )
  );
  const today = computed(() =>
    defaultBoardDate(
      now.value,
      zone.value,
      restaurant.selectedBranch?.businessDayStartHour
    )
  );
  const date = computed(() => {
    const value = proxy.$route.query.date;
    if (typeof value === 'string') {
      try {
        return addDays(value, 0);
      } catch {
        /* Ignore malformed deep-link dates. */
      }
    }
    return today.value;
  });
  const enabled = computed(() => store.day?.settingsSummary.enabled !== false);
  /** Full-page setup guide (D1): `?setup=1` for users who can change the settings. */
  const setupMode = computed(
    () => proxy.$route.query.setup === '1' && canSettings.value
  );
  const guideAutoOpen = useReservationGuideAutoOpen();
  /** D23: an enabled branch that never took a reservation opens on the guide. */
  watch(
    () => [store.day, restaurant.tables] as const,
    ([day, tables]) => {
      if (!day || !tables || !canSettings.value) return;
      const restaurantId = restaurant.restaurant?.id;
      const branchId = restaurant.selectedBranch?.id;
      if (!restaurantId || !branchId || store.dayBranchId !== branchId) return;
      const scopeKey = `${restaurantId}:${branchId}`;
      const incomplete = isReservationSetupIncomplete(
        day.settingsSummary.shiftsCount,
        day.shifts.length,
        tables.filter(
          (table) =>
            table.restaurantId === restaurantId && table.branchId === branchId
        )
      );
      // Already in the guide for this branch (reload, bookmark, branch switch
      // inside it): this visit has decided, so «Cerrar» never bounces back in.
      if (setupMode.value) {
        guideAutoOpen.claim(scopeKey, day.settingsSummary, incomplete);
        return;
      }
      const query = proxy.$route.query;
      if (
        query.tab !== undefined ||
        query.setup !== undefined ||
        query.r !== undefined ||
        query.date !== undefined
      )
        return;
      if (guideAutoOpen.claim(scopeKey, day.settingsSummary, incomplete))
        openSetup();
    }
  );
  const wide = computed(
    () =>
      width.value >= 1280 &&
      tab.value === 'today' &&
      enabled.value &&
      !setupMode.value
  );
  const waitingCount = computed(
    () =>
      waitlistCount.value ??
      store.day?.waitlist.filter((entry) => entry.status === 'waiting')
        .length ??
      0
  );
  const canPrint = computed(
    () => deviceCanEverPrint && printing.isPrinterConnected(PrinterType.Ticket)
  );
  let disposed = false;
  function i18nT(
    key: string,
    values?: Record<string, string | number>
  ): string {
    return String(proxy.$t(key, values));
  }
  async function replaceQuery(patch: Record<string, string | undefined>) {
    try {
      await proxy.$router.replace({
        query: { ...proxy.$route.query, ...patch },
      });
    } catch (error) {
      if (!disposed && !isNavigationFailure(error)) ui.serverError(error);
    }
  }
  /** Like replaceQuery, but adds a history entry (browser back undoes it). */
  async function pushQuery(patch: Record<string, string | undefined>) {
    try {
      await proxy.$router.push({
        query: { ...proxy.$route.query, ...patch },
      });
    } catch (error) {
      if (!disposed && !isNavigationFailure(error)) ui.serverError(error);
    }
  }
  function setTab(value: string) {
    // A section deep link belongs to the tab it was opened with.
    if (tabs.value.includes(value))
      void replaceQuery({ tab: value, section: undefined });
  }
  /** Enters the step-by-step guide (a history entry, so browser back closes it). */
  function openSetup() {
    void pushQuery({ setup: '1', r: undefined, section: undefined });
  }
  /** Leaves the guide; a target also picks the tab (and settings card) to land on. */
  function exitSetup(target?: IReservationGuideExitTarget) {
    void replaceQuery({
      setup: undefined,
      ...(target
        ? {
            tab: tabs.value.includes(target.tab) ? target.tab : tabs.value[0],
            section: target.section,
          }
        : {}),
    });
  }
  function setDate(value: string) {
    void replaceQuery({ date: value, r: undefined });
  }
  function select(id: number | null) {
    void replaceQuery({ r: id === null ? undefined : String(id) });
  }
  async function selectReservation(row: IReservation) {
    if (row.restaurantId !== restaurant.restaurant?.id) return;
    if (row.branchId !== restaurant.selectedBranch?.id) {
      const branch = restaurant.branches?.find(
        (value) => value.id === row.branchId
      );
      if (!branch || !(await confirmReservationBranchLeave()) || disposed)
        return;
      vueData.setCurrentBranch(branch);
      await nextTick();
    }
    if (!disposed)
      await replaceQuery({
        b: String(row.branchId),
        date: row.serviceDate,
        r: String(row.id),
      });
  }
  async function reload() {
    if (
      !canManage.value ||
      !restaurant.restaurant?.id ||
      !restaurant.selectedBranch?.id
    )
      return;
    try {
      await Promise.all([
        store.loadDay(date.value, { silent: !!store.day }),
        store.loadPendingCount(),
      ]);
    } catch (error) {
      if (!disposed)
        ui.serverError(error, i18nT('reservations.page.loadError'));
    }
  }
  function modalOptions(name: string, modalWidth: number) {
    return {
      name,
      adaptive: true,
      height: 'auto',
      scrollable: true,
      width: modalWidth,
      maxWidth: Math.round(width.value * 0.95),
      clickToClose: false,
      classes: 'dialog--height-limit',
    };
  }
  function openForm(prefill: IFormPrefill = {}) {
    if (canManage.value)
      modals.show(
        V2ReservationFormModal,
        { date: date.value, ...prefill },
        modalOptions('v2-reservation-form', 720),
        { saved: selectReservation }
      );
  }
  function editReservation(reservationToEdit: IReservation) {
    openForm({ reservationToEdit });
  }
  function openWalkIn() {
    if (canManage.value)
      modals.show(
        V2ReservationWalkInModal,
        {},
        modalOptions('v2-reservation-walk-in', 600),
        { saved: selectReservation, waitlist: openWaitlist }
      );
  }
  useReservationsShortcuts({
    enabled: () => canManage.value && enabled.value && !setupMode.value,
    isTodayTab: () => tab.value === 'today',
    openForm,
    openWalkIn,
    focusSearch: async () => {
      if (tab.value !== 'today') await replaceQuery({ tab: 'today' });
      await nextTick();
      if (!disposed) await dayTab.value?.focusSearch();
    },
    shiftDate: (days) => setDate(addDays(date.value, days)),
    goToday: () => {
      void replaceQuery({ tab: 'today', date: today.value, r: undefined });
    },
  });
  function openWaitlist(partySize?: number) {
    if (canManage.value)
      modals.show(
        V2ReservationWaitlistAddModal,
        { partySize },
        modalOptions('v2-reservation-waitlist-add', 480),
        {
          saved: () => {
            setTab('waitlist');
            void store.refreshActive();
          },
        }
      );
  }
  function openGuest(customerId: number) {
    if (canManage.value)
      modals.show(
        V2ReservationGuestModal,
        { customerId },
        modalOptions('v2-reservation-guest', 680),
        {
          select: selectReservation,
          create: (guest: IFormPrefill['guest']) => openForm({ guest }),
          saved: () => {
            if (!disposed) {
              void guestsTab.value?.reload();
              void store.refreshActive();
            }
          },
        }
      );
  }
  async function printDay() {
    if (
      !canManage.value ||
      !canPrint.value ||
      !store.day ||
      !restaurant.restaurant ||
      !restaurant.selectedBranch
    )
      return;
    try {
      await printing.printReservationsDayList(
        buildReservationsDayPrintOps({
          day: store.day,
          restaurantName: restaurant.restaurant.name,
          branchName: restaurant.selectedBranch.name,
          timeZone: zone.value,
          locale: proxy.$i18n.locale,
          t: i18nT,
          formatMoney: money.formatComputedMoney,
        }),
        i18nT('reservations.bar.print')
      );
    } catch (error) {
      if (!disposed) ui.serverError(error);
    }
  }
  watch(
    () => [proxy.$route.query.r, canManage.value],
    () => {
      const value = proxy.$route.query.r;
      const id =
        typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : 0;
      store.select(
        canManage.value && Number.isSafeInteger(id) && id > 0 ? id : null
      );
    },
    { immediate: true }
  );
  watch(
    () => [
      restaurant.restaurant?.id,
      restaurant.selectedBranch?.id,
      date.value,
      canManage.value,
    ],
    () => {
      void reload();
    },
    { immediate: true }
  );
  watch(
    () => restaurant.selectedBranch?.id,
    (id, previous) => {
      waitlistCount.value = null;
      if (previous && id !== previous && proxy.$route.query.r)
        void replaceQuery({ r: undefined });
    }
  );
  onScopeDispose(() => {
    disposed = true;
  });
</script>
<template>
  <div class="reservations-page">
    <header v-if="!setupMode" class="page-header">
      <h1>{{ $t('reservations') }}</h1>
      <V2ReservationsTabs
        :value="tab"
        :can-manage="canManage"
        :can-settings="canSettings"
        :waiting-count="waitingCount"
        :pending-count="store.pendingCount ?? 0"
        @input="setTab"
      />
    </header>
    <div
      class="reservations-workspace"
      :class="{ 'wide-board': wide && tab === 'today' && !!store.day }"
    >
      <main
        :id="`reservations-pane-${tab}`"
        class="reservations-pane"
        :class="{ 'board-pane': wide && tab === 'today' && !!store.day }"
        :role="setupMode ? undefined : 'tabpanel'"
        :aria-labelledby="setupMode ? undefined : `reservations-tab-${tab}`"
      >
        <V2ReservationsGuide v-if="setupMode" @exit="exitSetup" />
        <V2ReservationsSettingsTab
          v-else-if="tab === 'settings' && canSettings"
          @setup="openSetup"
        />
        <div
          v-else-if="!store.day && store.dayStatus === 'loading'"
          class="page-loading"
          aria-busy="true"
        >
          <PosUiSkeleton v-for="index in 5" :key="index" class="row-skeleton" />
        </div>
        <PosUiEmptyState
          v-else-if="!store.day && store.dayStatus === 'error'"
          icon="alert-circle-outline"
          :title="$t('reservations.page.loadError')"
          ><template #action
            ><PosUiButton icon="refresh" @click="reload">{{
              $t('reservations.page.retry')
            }}</PosUiButton></template
          ></PosUiEmptyState
        >
        <V2ReservationsHero
          v-else-if="!enabled"
          :can-settings="canSettings"
          @configure="openSetup"
        />
        <template v-else-if="canManage">
          <V2ReservationsDayTab
            ref="dayTab"
            v-if="tab === 'today'"
            :date="date"
            :today="today"
            :can-print="canPrint"
            :can-settings="canSettings"
            @date="setDate"
            @select="select"
            @create="openForm"
            @walkIn="openWalkIn"
            @print="printDay"
            @setup="openSetup"
          />
          <V2ReservationsAgendaTab
            v-else-if="tab === 'agenda'"
            :pending-first="(store.pendingCount ?? 0) > 0"
            @select="selectReservation"
            @create="openForm()"
          />
          <V2ReservationsWaitlistTab
            v-else-if="tab === 'waitlist'"
            @create="openForm"
            @select="selectReservation"
            @count="waitlistCount = $event"
            @add="openWaitlist()"
          />
          <V2ReservationsGuestsTab
            ref="guestsTab"
            v-else-if="tab === 'guests'"
            @guest="openGuest"
          />
          <V2ReservationsReportsTab v-else-if="tab === 'reports'" />
        </template>
      </main>
      <V2ReservationDetailPanel
        v-if="canManage && enabled && !setupMode"
        :reservation-id="store.selectedReservationId"
        :mode="wide ? 'column' : 'sheet'"
        @close="select(null)"
        @edit="editReservation"
        @guest="openGuest"
      />
    </div>
  </div>
</template>
<style scoped lang="scss">
  .reservations-page {
    @apply tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-bg-canvas;
    .page-header {
      @apply tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-3 md:tw-px-5;
      h1 {
        @apply tw-m-0 tw-hidden tw-text-xl tw-font-semibold tw-text-neutral-900 md:tw-block;
      }
    }
    .reservations-workspace {
      @apply tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1;
    }
    .reservations-pane {
      @apply tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-1;
    }
    .page-loading {
      @apply tw-p-4;
      .row-skeleton {
        @apply tw-mb-3 tw-h-14 tw-w-full;
      }
    }
    .wide-board {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 26rem;
      grid-template-rows: auto auto auto minmax(0, 1fr);
      grid-template-areas: 'command command' 'filters detail' 'notice detail' 'content detail';
      .board-pane {
        display: contents;
      }
      :deep(.reservations-day-tab) {
        display: contents;
      }
      :deep(.reservations-command-bar) {
        grid-area: command;
      }
      :deep(.status-filters) {
        grid-area: filters;
      }
      :deep(.setup-notice) {
        grid-area: notice;
      }
      :deep(.day-content) {
        grid-area: content;
      }
      :deep(.reservation-detail) {
        grid-area: detail;
      }
    }
  }
</style>
